
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronLeft } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { documentTypeApi } from "@/services/api";
import { useToast } from "@/components/ui/use-toast";
import DocumentTypeForm from "@/components/documents/DocumentTypeForm";
import { useAuth } from "@/contexts/AuthContext";

const DocumentTypeEdit = () => {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { selectedRole } = useAuth();
  
  // Check if id is present and convert to number
  const documentTypeId = id ? parseInt(id) : undefined;
  
  // Fetch document type details
  const { 
    data: documentType, 
    isLoading, 
    isError, 
    error,
    refetch
  } = useQuery({
    queryKey: ['documentType', documentTypeId],
    queryFn: () => documentTypeId ? documentTypeApi.getById(documentTypeId) : Promise.reject('No document type ID provided'),
    enabled: !!documentTypeId,
    meta: {
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Error loading document type",
          description: error.message || "Failed to fetch document type details",
        });
      }
    }
  });
  
  // Function to handle document type update success
  const handleUpdateSuccess = () => {
    toast({
      title: "Document type updated",
      description: "The document type has been updated successfully.",
    });
    navigate("/backoffice/settings?tab=document-types");
  };

  const handleCancel = () => {
    navigate("/backoffice/settings?tab=document-types");
  };

  if (isLoading) {
    return (
      <BackofficeLayout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
        </div>
      </BackofficeLayout>
    );
  }

  if (isError || !documentType) {
    return (
      <BackofficeLayout>
        <div className="space-y-6">
          <Alert variant="destructive">
            <AlertDescription>
              {(error as any)?.message || "Failed to load document type. The document type may have been deleted or you don't have permission to view it."}
            </AlertDescription>
          </Alert>
        </div>
      </BackofficeLayout>
    );
  }

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="space-y-1">
            <Button 
              asChild 
              variant="outline" 
              size="sm" 
              className="mb-2"
              onClick={() => navigate("/backoffice/settings?tab=document-types")}
            >
              <Link to="/backoffice/settings?tab=document-types">
                <ChevronLeft className="mr-1 h-4 w-4" />
                Back to Document Types
              </Link>
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">
              {/* Platform types are shared config: a management company can
                  USE them but not change them (8-Sep: "los pueden usar, no
                  editar"). */}
              {selectedRole?.type === "management_company" &&
              !(documentType as any).management_company_id
                ? `Document Type: ${documentType.name}`
                : `Edit Document Type: ${documentType.name}`}
            </h1>
            <p className="text-gray-500">
              {selectedRole?.type === "management_company" &&
              !(documentType as any).management_company_id
                ? "Platform type — your company can use it, but only the platform edits it."
                : "Make changes to the document type details"}
            </p>
          </div>
          {!(
            selectedRole?.type === "management_company" &&
            !(documentType as any).management_company_id
          ) && (
            <Button type="submit" form="document-type-edit-form">
              Update Document Type
            </Button>
          )}
        </div>

        <fieldset
          disabled={
            selectedRole?.type === "management_company" &&
            !(documentType as any).management_company_id
          }
        >
          <DocumentTypeForm
            documentType={documentType}
            onSuccess={handleUpdateSuccess}
            onCancel={handleCancel}
            formId="document-type-edit-form"
          />
        </fieldset>
      </div>
    </BackofficeLayout>
  );
};

export default DocumentTypeEdit;
